import {
  Box,
  DropdownButtonContext,
  InputRichText,
} from '@dixa/lib-shared-habla';
import { conversationLanguages } from 'lib-shared-languages';
import { memo, useContext } from 'react';
import { useMount } from 'react-use';

import { AiLoadingState } from 'components/aiLoadingState';
import { useEditorContext } from 'utils/useEditorContext';
import { useTranslateText } from 'utils/useTranslateText';

import { EditorCopilotDropdownTranslate } from '../translate';

interface EditorCopilotDropdownTranslateToConversationLanguageProps {
  languageCode: string;
  csid: string;
}

/**
 * Represents a component that translates the selection into conversation
 * language.
 */
export const EditorCopilotDropdownTranslateToConversationLanguage = memo(
  function EditorCopilotDropdownTranslateToConversationLanguage({
    languageCode,
    csid,
  }: EditorCopilotDropdownTranslateToConversationLanguageProps) {
    const { replace, getSelectedText } = useEditorContext();
    const dropdownContext = useContext(DropdownButtonContext);
    const { content } = useContext(InputRichText.Context);
    const [translate] = useTranslateText();
    const text = getSelectedText();
    const language = conversationLanguages.find(({ code }) =>
      languageCode.startsWith(code),
    )?.name;

    const closeDropdownAndPerformTranslation = async (language: string) => {
      dropdownContext.closeDropdown();
      content.setUiBlocker(
        <Box style={{ position: 'absolute', bottom: 16 }}>
          <AiLoadingState label={`Translating into ${language}`} />
        </Box>,
      );

      try {
        const response = await translate({
          language,
          text,
          csid,
          direction: 'outbound',
        });
        content.setUiBlocker(null);
        if (response.translatedText) {
          replace(response.translatedText);
        }
      } catch {
        content.setUiBlocker(null);
      }
    };

    useMount(() => {
      if (language && text) {
        // If the language of the conversation is detected, translate instantly
        // and close the dropdown.
        // Closing the dropdown will unmount this component, while the translation
        // request is being processed. Hence, in `develop` mode, React will show
        // a warning regarding memory leak. However, this is not the case and
        // there is no memory leak whatsoever.
        // Unfortunately there is no way to suppress that warning. Disregard
        // that warning for this component.
        closeDropdownAndPerformTranslation(language);
      }
    });

    if (language) {
      return null;
    }

    return (
      <EditorCopilotDropdownTranslate initialText="Unable to detect conversation's language. Please select a language from the list above." />
    );
  },
);
